// ── Welle D3b: Board-Layout laden/speichern ──
// Quelle der Wahrheit ist der Server (D3a: GET/PUT/DELETE /api/dashboard/layout[/:modell]).
// localStorage hält je Lehrkraft eine Kopie, damit das Board sofort steht; danach gleicht der
// Server-Stand ab. Gespeichert wird NUR nach Handlungen der Lehrkraft (verschieben, Größe,
// ausblenden, hinzufügen, rückgängig) — Ziehen gebündelt nach ~800 ms, Einzelaktionen sofort.
// Noch nicht bestätigte Änderungen liegen zusätzlich als „ausstehend“ im localStorage: Wird die
// Seite vorher verlassen (der keepalive-PUT ist dabei nicht verlässlich), schickt das nächste
// Öffnen sie nach — sie haben dann Vorrang vor dem älteren Server-Stand.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { holeToken } from '../../../lib/sitzung';
import { ApiError, apiFetch, apiGet } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import type { Katalog, Modell, PcLayout, MobilLayout, WidgetDef } from './layout-logik';

type Stand<L> = { layout: L; standard: boolean };
type Daten = { katalog: Katalog; pc: Stand<PcLayout>; mobil: Stand<MobilLayout> };

const ALTER_WIDGET_KEY = 'lehrerdesk-widgets'; // Auswahl aus den Einstellungen (vor D3b)
const SPEICHER_VERZOEGERUNG = 800;

/** Lehrer-ID aus dem Anmelde-Token — der Zwischenspeicher gilt nur für diese Lehrkraft. */
function lehrerIdAusToken(): string | null {
  try {
    const t = holeToken();
    const teil = t?.split('.')[1];
    if (!teil) return null;
    const json = JSON.parse(window.atob(teil.replace(/-/g, '+').replace(/_/g, '/')));
    return json?.id != null ? String(json.id) : null;
  } catch { return null; }
}
const cacheKey = () => { const id = lehrerIdAusToken(); return id ? `ld-board-v1-${id}` : null; };

function leseCache(): Daten | null {
  const k = cacheKey();
  if (!k) return null;
  try { const d = JSON.parse(localStorage.getItem(k) ?? 'null'); return d?.katalog && d?.pc && d?.mobil ? d : null; } catch { return null; }
}
function schreibeCache(d: Daten) {
  const k = cacheKey();
  if (!k) return;
  try { localStorage.setItem(k, JSON.stringify(d)); } catch { /* voll/gesperrt — egal, der Server hat den Stand */ }
}

type Ausstehend = { pc?: PcLayout; mobil?: MobilLayout };
const ausstehendKey = () => { const id = lehrerIdAusToken(); return id ? `ld-board-ausstehend-v1-${id}` : null; };
function leseAusstehend(): Ausstehend {
  const k = ausstehendKey();
  if (!k) return {};
  try { return JSON.parse(localStorage.getItem(k) ?? '{}') ?? {}; } catch { return {}; }
}
function setzeAusstehend(modell: Modell, layout: PcLayout | MobilLayout | null) {
  const k = ausstehendKey();
  if (!k) return;
  try {
    const a = leseAusstehend() as Record<string, unknown>;
    if (layout) a[modell] = layout; else delete a[modell];
    if (Object.keys(a).length) localStorage.setItem(k, JSON.stringify(a)); else localStorage.removeItem(k);
  } catch { /* egal */ }
}
/** Erledigt nur, wenn noch genau dieser Stand aussteht (sonst ist schon ein neuerer unterwegs) */
function erledigeAusstehend(modell: Modell, layout: PcLayout | MobilLayout) {
  if (JSON.stringify(leseAusstehend()[modell]) === JSON.stringify(layout)) setzeAusstehend(modell, null);
}

/**
 * Baustein 5: alte Auswahl aus den Einstellungen einmalig übernehmen — nur für Geräte-Modelle,
 * die noch das Standard-Layout haben. Abgewählte Karten werden ausgeblendet. Ändert sich nichts,
 * bleibt „standard“ erhalten (kein PUT). Der alte Schlüssel wird danach gelöscht.
 */
function migriereAlteAuswahl<L extends PcLayout | MobilLayout>(stand: Stand<L>): L | null {
  let alt: Record<string, boolean> | null = null;
  try { alt = JSON.parse(localStorage.getItem(ALTER_WIDGET_KEY) ?? 'null'); } catch { alt = null; }
  if (!alt || !stand.standard) return null;
  const weg: string[] = [];
  if (alt.neueMails === false) weg.push('mails');
  if (alt.anstehendeAktionen === false) weg.push('aktionen');
  const raus = weg.filter(id => (stand.layout.widgets as { id: string }[]).some(w => w.id === id));
  if (raus.length === 0) return null;
  return {
    widgets: (stand.layout.widgets as { id: string }[]).filter(w => !raus.includes(w.id)),
    ausgeblendet: [...raus, ...stand.layout.ausgeblendet.filter(id => !raus.includes(id))],
  } as L;
}

export function useBoard() {
  const [daten, setDaten] = useState<Daten | null>(() => leseCache());
  const [ladeFehler, setLadeFehler] = useState(false);
  const datenRef = useRef(daten);
  datenRef.current = daten;
  const offen = useRef<{ [M in Modell]?: { layout: PcLayout | MobilLayout; timer: number } }>({});

  // Speichervorgänge je Gerätetyp nacheinander — ein späterer Stand überholt nie einen früheren
  const kette = useRef<{ [M in Modell]: Promise<void> }>({ pc: Promise.resolve(), mobil: Promise.resolve() });
  const senden = useCallback((modell: Modell, layout: PcLayout | MobilLayout, keepalive = false): Promise<void> => {
    // Beim Verlassen der Seite (keepalive) sofort und synchron losschicken — ein Umweg über
    // die Warteschlange liefe erst, wenn die Seite schon weg ist
    const lauf = keepalive
      ? sendeEinmal(modell, layout, true)
      : kette.current[modell].then(() => sendeEinmal(modell, layout, false));
    kette.current[modell] = lauf;
    return lauf;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const sendeEinmal = async (modell: Modell, layout: PcLayout | MobilLayout, keepalive: boolean) => {
    try {
      await apiFetch(`/api/dashboard/layout/${modell}`, {
        method: 'PUT', body: JSON.stringify(layout), keepalive, // Header (Token) setzt apiFetch
      });
      erledigeAusstehend(modell, layout);
    } catch (e) {
      // Vom Server abgelehnt (4xx): nicht bei jedem Öffnen erneut versuchen
      if (e instanceof ApiError && e.status >= 400 && e.status < 500) erledigeAusstehend(modell, layout);
      if (keepalive) return;
      toast('Anordnung nicht gespeichert', 'error', {
        aktion: { label: 'Erneut versuchen', onClick: () => { void senden(modell, layout); } },
        dauerMs: 8000,
      });
    }
  };

  const jetztSenden = useCallback((modell: Modell, keepalive = false) => {
    const o = offen.current[modell];
    if (!o) return;
    window.clearTimeout(o.timer);
    delete offen.current[modell];
    void senden(modell, o.layout, keepalive);
  }, [senden]);

  // Laden: Katalog + beide Layouts; danach ggf. alte Einstellungs-Auswahl übernehmen
  useEffect(() => {
    let aktiv = true;
    Promise.all([
      apiGet<Katalog>('/api/dashboard/widgets'),
      apiGet<{ pc: Stand<PcLayout>; mobil: Stand<MobilLayout> }>('/api/dashboard/layout'),
    ]).then(([katalog, layouts]) => {
      if (!aktiv) return;
      let pc = layouts.pc, mobil = layouts.mobil;
      // Beim letzten Mal nicht mehr bestätigte Änderungen nachsenden
      const aus = leseAusstehend();
      if (aus.pc) { pc = { layout: aus.pc, standard: false }; void senden('pc', aus.pc); }
      if (aus.mobil) { mobil = { layout: aus.mobil, standard: false }; void senden('mobil', aus.mobil); }
      if (localStorage.getItem(ALTER_WIDGET_KEY) !== null) {
        const pcNeu = migriereAlteAuswahl(pc);
        const mobilNeu = migriereAlteAuswahl(mobil);
        if (pcNeu) { pc = { layout: pcNeu, standard: false }; void senden('pc', pcNeu); }
        if (mobilNeu) { mobil = { layout: mobilNeu, standard: false }; void senden('mobil', mobilNeu); }
        try { localStorage.removeItem(ALTER_WIDGET_KEY); } catch { /* egal */ }
      }
      // Noch nicht gesendete eigene Änderungen nicht vom Server-Stand überschreiben lassen
      const vorher = datenRef.current;
      const d: Daten = {
        katalog,
        pc: offen.current.pc && vorher ? vorher.pc : pc,
        mobil: offen.current.mobil && vorher ? vorher.mobil : mobil,
      };
      setDaten(d); schreibeCache(d); setLadeFehler(false);
    }).catch(() => { if (aktiv) setLadeFehler(true); });
    return () => { aktiv = false; };
  }, [senden]);

  // Beim Verlassen (Seitenwechsel, Neuladen, Tab zu) nicht auf die Bündelung warten
  useEffect(() => {
    const raus = () => { jetztSenden('pc', true); jetztSenden('mobil', true); };
    window.addEventListener('pagehide', raus);
    return () => { window.removeEventListener('pagehide', raus); raus(); };
  }, [jetztSenden]);

  /**
   * Layout übernehmen und speichern. Ziehen/Größe per Ecke/Sortieren: gebündelt (~800 ms).
   * Einzelaktionen (Ausblenden, Rückgängig, +, Größe per Menü): sofort.
   */
  const speichere = useCallback(<M extends Modell>(modell: M, layout: M extends 'pc' ? PcLayout : MobilLayout, sofort = false) => {
    const vorher = datenRef.current;
    if (!vorher) return;
    const d = { ...vorher, [modell]: { layout, standard: false } } as Daten;
    datenRef.current = d;
    setDaten(d); schreibeCache(d);
    setzeAusstehend(modell, layout);
    const o = offen.current[modell];
    if (o) window.clearTimeout(o.timer);
    offen.current[modell] = {
      layout,
      timer: window.setTimeout(() => jetztSenden(modell), SPEICHER_VERZOEGERUNG),
    };
    if (sofort) jetztSenden(modell);
  }, [jetztSenden]);

  /** Standard-Anordnung des Geräte-Modells wiederherstellen (DELETE) */
  const zuruecksetzen = useCallback(async (modell: Modell) => {
    const o = offen.current[modell];
    if (o) { window.clearTimeout(o.timer); delete offen.current[modell]; }
    setzeAusstehend(modell, null);
    await kette.current[modell].catch(() => {}); // laufende Speicherungen erst abschließen
    const r = await apiFetch<Stand<PcLayout | MobilLayout>>(`/api/dashboard/layout/${modell}`, { method: 'DELETE' });
    const vorher = datenRef.current;
    if (!vorher) return;
    const d = { ...vorher, [modell]: r } as Daten;
    datenRef.current = d;
    setDaten(d); schreibeCache(d);
  }, []);

  const defs = useMemo(() => new Map<string, WidgetDef>((daten?.katalog.widgets ?? []).map(w => [w.id, w])), [daten?.katalog]);

  return { daten, defs, ladeFehler, speichere, zuruecksetzen };
}

